const isFirebasePasswordAuthEnabled = () => (
  process.env.REACT_APP_FIREBASE_PASSWORD_AUTH === 'true'
);

let firebaseAuthPromise;

const getFirebaseConfig = () => ({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID
});

const assertFirebaseConfiguration = config => {
  const requiredFields = ['apiKey', 'authDomain', 'projectId', 'appId'];
  const missingFields = requiredFields.filter(field => !config[field]);

  if (missingFields.length) {
    throw new Error(`Firebase password authentication is not configured: ${missingFields.join(', ')}`);
  }
};

const getFirebaseAuthModules = () => {
  if (!isFirebasePasswordAuthEnabled()) {
    return Promise.reject(new Error('Firebase password authentication is disabled.'));
  }

  if (!firebaseAuthPromise) {
    firebaseAuthPromise = Promise.all([
      import('firebase/app'),
      import('firebase/auth')
    ]).then(([firebaseModule]) => {
      const config = getFirebaseConfig();
      assertFirebaseConfiguration(config);
      const firebase = firebaseModule.default || firebaseModule;

      const appName = 'goldapi-password-auth';
      const app = firebase.apps.find(existingApp => existingApp.name === appName)
        || firebase.initializeApp(config, appName);

      return {
        auth: app.auth()
      };
    }).catch(err => {
      // Permit a corrected configuration or transient SDK load failure to retry.
      firebaseAuthPromise = null;
      throw err;
    });
  }

  return firebaseAuthPromise;
};

const requireCurrentUser = auth => {
  if (!auth.currentUser) {
    const err = new Error('No Firebase password user is signed in.');
    err.code = 'auth/no-current-user';
    throw err;
  }
  return auth.currentUser;
};

const FirebasePasswordAuth = {
  isEnabled: isFirebasePasswordAuthEnabled,

  initialize: async () => {
    const { auth } = await getFirebaseAuthModules();
    return auth;
  },

  waitForAuthState: async () => {
    const { auth } = await getFirebaseAuthModules();
    return new Promise((resolve, reject) => {
      let unsubscribe = () => {};
      unsubscribe = auth.onAuthStateChanged(
        user => {
          unsubscribe();
          resolve(user);
        },
        err => {
          unsubscribe();
          reject(err);
        }
      );
    });
  },

  signIn: async (email, password) => {
    const { auth } = await getFirebaseAuthModules();
    return auth.signInWithEmailAndPassword(email, password);
  },

  createAccount: async (email, password) => {
    const { auth } = await getFirebaseAuthModules();
    return auth.createUserWithEmailAndPassword(email, password);
  },

  getIdToken: async (forceRefresh = false) => {
    const { auth } = await getFirebaseAuthModules();
    return requireCurrentUser(auth).getIdToken(forceRefresh);
  },

  reloadCurrentUser: async () => {
    const { auth } = await getFirebaseAuthModules();
    const user = requireCurrentUser(auth);
    await user.reload();
    return requireCurrentUser(auth);
  },

  sendVerificationEmail: async () => {
    const { auth } = await getFirebaseAuthModules();
    return requireCurrentUser(auth).sendEmailVerification();
  },

  sendPasswordResetEmail: async email => {
    const { auth } = await getFirebaseAuthModules();
    return auth.sendPasswordResetEmail(email);
  },

  logOut: async () => {
    const { auth } = await getFirebaseAuthModules();
    return auth.signOut();
  }
};

export default FirebasePasswordAuth;
